import React from 'react';
import { motion } from 'framer-motion';
import { Lock, BookOpen, MessageCircle, LayoutGrid, ShieldCheck, BarChart2, Leaf, ChevronRight, Check } from 'lucide-react';
import { MODULES } from '../data/scenarios';

const ICON_MAP = {
  book: BookOpen,
  chat: MessageCircle,
  grid: LayoutGrid,
  shield: ShieldCheck,
  chart: BarChart2,
  leaf: Leaf,
};

const COLOR_MAP = {
  blue: { bg: 'hsl(201 82% 94%)', icon: 'hsl(201 82% 35%)', border: 'hsl(201 82% 80%)' },
  teal: { bg: 'hsl(174 55% 92%)', icon: 'hsl(174 55% 36%)', border: 'hsl(174 55% 70%)' },
  indigo: { bg: 'hsl(235 70% 94%)', icon: 'hsl(235 70% 45%)', border: 'hsl(235 70% 75%)' },
  orange: { bg: 'hsl(28 90% 94%)', icon: 'hsl(28 90% 50%)', border: 'hsl(28 90% 75%)' },
  purple: { bg: 'hsl(270 60% 94%)', icon: 'hsl(270 60% 48%)', border: 'hsl(270 60% 75%)' },
  green: { bg: 'hsl(145 60% 93%)', icon: 'hsl(145 60% 38%)', border: 'hsl(145 60% 70%)' },
};

const ModulesScreen = ({ onSelectModule, completedModules = [] }) => {
  return (
    <div className="min-h-screen" style={{ background: 'hsl(var(--background))' }}>
      {/* Header */}
      <div className="px-6 pt-10 pb-8" style={{ background: 'hsl(var(--card))', borderBottom: '1.5px solid hsl(var(--border))' }}>
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'var(--gradient-ocean)' }}
            >
              <span style={{ color: 'hsl(0 0% 100%)', fontSize: 18 }}>🌊</span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'hsl(var(--muted-foreground))' }}>Синомарин</p>
              <h2 className="text-lg font-semibold" style={{ color: 'hsl(var(--foreground))' }}>Обучение для аптек</h2>
            </div>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>
            Пройдите модули в удобном порядке. Некоторые откроются после завершения предыдущих.
          </p>
        </div>
      </div>

      {/* Modules grid */}
      <div className="px-6 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-2 gap-4">
            {MODULES.map((module, i) => {
              const IconComp = ICON_MAP[module.icon] || BookOpen;
              const colors = COLOR_MAP[module.color] || COLOR_MAP.blue;
              const isCompleted = completedModules.includes(module.id);
              const isMain = module.isMain;

              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  onClick={() => !module.locked && onSelectModule(module.id)}
                  className={`module-card p-5 flex flex-col min-h-[180px] ${module.locked ? 'module-locked' : ''} ${isMain ? 'col-span-2' : ''}`}
                  style={{
                    borderColor: isMain ? 'hsl(174 55% 60%)' : isCompleted ? 'hsl(145 65% 60%)' : 'hsl(var(--border))',
                    background: isMain ? 'linear-gradient(135deg, hsl(174 55% 97%), hsl(201 82% 96%))' : 'hsl(var(--card))',
                  }}
                >
                  {/* Icon + Lock */}
                  <div className="flex items-start justify-between mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: colors.bg, border: `1.5px solid ${colors.border}` }}
                    >
                      {isCompleted
                        ? <Check size={20} color={colors.icon} />
                        : <IconComp size={20} color={colors.icon} />
                      }
                    </div>
                    <div className="flex items-center gap-2">
                      {isMain && (
                        <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full"
                          style={{ background: 'hsl(174 55% 36%)', color: 'hsl(0 0% 100%)' }}>
                          Старт
                        </span>
                      )}
                      {module.locked && <Lock size={15} style={{ color: 'hsl(var(--muted-foreground))' }} />}
                    </div>
                  </div>

                  {/* Text */}
                  <div className="flex flex-col flex-1">
                    <p className="text-sm font-semibold mb-0.5" style={{ color: 'hsl(var(--foreground))' }}>{module.title}</p>
                    <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{module.subtitle}</p>

                    {isMain && (
                      <p className="text-xs mt-2 leading-relaxed" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        {module.description}
                      </p>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: '1px solid hsl(var(--border))' }}>
                    <span className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>{module.duration}</span>
                    {!module.locked && (
                      <ChevronRight size={15} style={{ color: isMain ? 'hsl(174 55% 36%)' : 'hsl(var(--muted-foreground))' }} />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesScreen;
